import React from 'react';
import { motion } from 'framer-motion';

interface SectionProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  centered?: boolean;
  id?: string;
  variant?: 'default' | 'light' | 'dark' | 'gradient'; // Arka plan seçenekleri
  withPattern?: boolean; // Arka plan deseni olsun mu?
}

const Section: React.FC<SectionProps> = ({
  children,
  className = '',
  title,
  subtitle,
  centered = false,
  id,
  variant = 'default',
  withPattern = false,
}) => {

  // Varyanta göre arka plan ve metin renklerini belirle
  const styles = {
    default: 'bg-white text-primary-900',
    light: 'bg-gray-50 text-primary-900',
    dark: 'bg-primary-900 text-white',
    gradient: 'bg-gradient-to-br from-primary-900 to-primary-800 text-white',
  };

  const subTextColors = {
    default: 'text-gray-600',
    light: 'text-gray-600',
    dark: 'text-primary-100',
    gradient: 'text-primary-100',
  };

  return (
    <section 
      id={id} 
      className={`relative py-20 md:py-32 overflow-hidden ${styles[variant]} ${className}`}
    >
      {/* Opsiyonel: Arka plan deseni (Hafif nokta dokusu) */}
      {withPattern && (
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none">
          <svg className="h-full w-full" width="100%" height="100%">
            <defs>
              <pattern id="dots-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots-pattern)" />
          </svg>
        </div>
      )}

      {/* Opsiyonel: Gradient modunda dekoratif ışık hüzmesi */}
      {variant === 'gradient' && (
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary-500/30 to-transparent"></div>
      )}

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Başlık Alanı */}
        {(title || subtitle) && (
          <div className={`mb-16 ${centered ? 'text-center mx-auto max-w-3xl' : 'max-w-3xl'}`}>
            
            {title && (
              <div className="relative inline-block mb-4">
                <motion.h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-display font-bold tracking-tight"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  {title}
                </motion.h2>
                
                {/* Başlık Altı Dekoratif Çizgi */}
                <motion.div 
                  className={`h-1.5 bg-secondary-400 rounded-full mt-2 ${centered ? 'mx-auto' : ''}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: centered ? 80 : 60 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                />
              </div>
            )}

            {subtitle && (
              <motion.p 
                className={`text-lg md:text-xl leading-relaxed ${subTextColors[variant]}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {subtitle}
              </motion.p>
            )}
          </div>
        )}

        {/* İçerik Alanı */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
};

export default Section;